import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Icon from './Icon';
import Logo from './Logo';
import { navFor, titleFor } from '../nav';
import { useAuth } from '../context/AuthContext';

function initials(name) {
  return (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const items = navFor(user?.role);

  // Everything about *this account* now hangs off the avatar rather than sitting in the
  // navigation, which is for places in the product. Closing on an outside click and on
  // Escape is what makes a menu a menu; without both it is a panel that will not go away.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  // The menu holds a route, so it has to close when that route is taken — otherwise it
  // is still hanging open over the page it just navigated to.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <div className="shell">
      <aside className={`sb${open ? ' open' : ''}`} aria-label="Main navigation">
        <div className="sb-brand">
          <Logo height={28} />
        </div>

        <nav className="sb-scroll">
          {items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) => `sb-item${isActive ? ' active' : ''}`}
              onClick={() => setOpen(false)}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="main-col">
        <header className="topbar">
          <button
            type="button"
            className="btn btn-secondary hide-lg"
            aria-label="Toggle navigation"
            onClick={() => setOpen((value) => !value)}
          >
            <Icon name="menu" />
          </button>
          <div className="topbar-title">
            <div className="eyebrow">{user?.roleLabel}</div>
            <strong>{titleFor(user?.role, location.pathname)}</strong>
          </div>
          <div className="spacer" />

          <div className="who-menu" ref={menuRef}>
            <button
              type="button"
              className="who"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((value) => !value)}
            >
              <span className="avatar">{initials(user?.fullName)}</span>
              <span className="who-name">
                <b>{user?.fullName}</b>
                <span>{user?.email ?? user?.mobile}</span>
              </span>
              <Icon name="chevron" size={15} className="who-caret" />
            </button>

            {menuOpen && (
              <div className="menu" role="menu">
                {/* Repeated inside the menu because the trigger hides the name on a
                    narrow header, where the avatar alone says nothing about whose
                    account is about to be signed out. */}
                <div className="menu-head">
                  <b>{user?.fullName}</b>
                  <span>{user?.roleLabel}</span>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  onClick={() => navigate('/profile')}
                >
                  <Icon name="gear" size={16} />
                  Profile &amp; Settings
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item menu-item-danger"
                  onClick={logout}
                >
                  <Icon name="logout" size={16} />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="page">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
